import { Button, Input } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Filter, Search, Tag, X, CheckSquare, Square } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, Conversation, Profile } from '../lib/admin-api';
import { DataTable } from '../components/admin/DataTable';

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export function ConversationsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [orgTags, setOrgTags] = useState<string[]>([]);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [agents, setAgents] = useState<Profile[]>([]);
  const [bulkAssignee, setBulkAssignee] = useState('');

  useEffect(() => {
    async function fetchData() {
      if (!profile?.organization_id) return;

      setLoading(true);
      const [data, tags] = await Promise.all([
        adminApi.getConversations(profile.organization_id, {
          status: statusFilter,
          search: searchQuery || undefined,
          tag: tagFilter || undefined,
        }),
        adminApi.getOrgTags(profile.organization_id),
      ]);
      setConversations(data);
      setOrgTags(tags);
      setLoading(false);
    }

    const debounce = setTimeout(fetchData, searchQuery ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [profile?.organization_id, statusFilter, searchQuery, tagFilter]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    adminApi.getAgents(profile.organization_id).then(setAgents);
  }, [profile?.organization_id]);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [conversations]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === conversations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(conversations.map((c) => c.id)));
    }
  };

  const handleBulkAction = async () => {
    if (selectedIds.size === 0 || !bulkAction) return;

    const ids = Array.from(selectedIds);
    let success = false;

    switch (bulkAction) {
      case 'resolve':
        success = await adminApi.bulkUpdateConversations(ids, { status: 'resolved' });
        break;
      case 'close':
        success = await adminApi.bulkUpdateConversations(ids, { status: 'closed' });
        break;
      case 'reopen':
        success = await adminApi.bulkUpdateConversations(ids, { status: 'open' });
        break;
      case 'assign':
        if (bulkAssignee) {
          success = await adminApi.bulkUpdateConversations(ids, {
            assigned_agent_id: bulkAssignee,
          });
        }
        break;
      case 'urgent':
        success = await adminApi.bulkUpdateConversations(ids, { priority: 'urgent' });
        break;
    }

    if (success) {
      // Refresh
      if (profile?.organization_id) {
        const data = await adminApi.getConversations(profile.organization_id, {
          status: statusFilter,
          search: searchQuery || undefined,
          tag: tagFilter || undefined,
        });
        setConversations(data);
      }
      setSelectedIds(new Set());
      setBulkAction('');
      setBulkAssignee('');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      open: 'bg-success text-success-foreground',
      pending: 'bg-warning text-warning-foreground',
      resolved: 'bg-info text-info-foreground',
      closed: 'bg-muted text-foreground',
      snoozed: 'bg-info text-info-foreground',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.open}`}>
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const styles: Record<string, string> = {
      low: 'text-muted-foreground',
      normal: 'text-info-foreground',
      high: 'text-warning-foreground',
      urgent: 'text-error-text font-semibold',
    };
    return <span className={styles[priority] || styles.normal}>{priority}</span>;
  };

  const columns = [
    {
      key: 'select',
      header: (
        <Button size="iconSm" variant="ghost" aria-label="Select all conversations" aria-pressed={selectedIds.size === conversations.length && conversations.length > 0} onClick={toggleSelectAll} className="p-0.5">
          {selectedIds.size === conversations.length && conversations.length > 0 ? (
            <CheckSquare className="w-4 h-4 text-link" />
          ) : (
            <Square className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      ),
      render: (conv: Conversation) => (
        <Button size="iconSm" variant="ghost" aria-label={`Select ${conv.title || "conversation"}`} aria-pressed={selectedIds.has(conv.id)}
          onClick={(e) => {
            e.stopPropagation();
            toggleSelect(conv.id);
          }}
          className="p-0.5"
        >
          {selectedIds.has(conv.id) ? (
            <CheckSquare className="w-4 h-4 text-link" />
          ) : (
            <Square className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      render: (conv: Conversation) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">
              {conv.title || `Conversation #${conv.id.slice(0, 8)}`}
            </span>
            {conv.ai_handled && (
              <Bot className="w-4 h-4 text-info-foreground" />
            )}
          </div>
          {conv.tags && conv.tags.length > 0 && (
            <div className="flex gap-1 mt-1">
              {conv.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-primary/15 text-link text-[10px] font-medium rounded"
                >
                  <Tag className="w-2.5 h-2.5" />
                  {tag}
                </span>
              ))}
              {conv.tags.length > 3 && (
                <span className="text-[10px] text-muted-foreground">+{conv.tags.length - 3}</span>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (conv: Conversation) => getStatusBadge(conv.status),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (conv: Conversation) => getPriorityBadge(conv.priority),
    },
    {
      key: 'channel',
      header: 'Channel',
      render: (conv: Conversation) => (
        <span className="text-muted-foreground capitalize">{conv.channel}</span>
      ),
    },
    {
      key: 'agent',
      header: 'Assigned To',
      render: (conv: Conversation) => (
        <span className="text-muted-foreground">
          {conv.agent?.display_name || 'Unassigned'}
        </span>
      ),
    },
    {
      key: 'updated_at',
      header: 'Last Activity',
      render: (conv: Conversation) => (
        <span className="text-muted-foreground">
          {new Date(conv.updated_at).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Conversations</h1>
          <p className="text-muted-foreground mt-1">Manage all support conversations</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-card p-4 rounded-lg border border-border flex-wrap">
        <Filter className="w-5 h-5 text-muted-foreground flex-shrink-0" />

        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search conversations" placeholder="Search conversations..."
            className="w-full pl-9 pr-14 py-1.5 border border-border rounded-md text-sm focus:ring-2 focus:ring-ring focus:border-primary"
          />
          {searchQuery && (
            <Button size="iconSm" variant="ghost"
              aria-label="Clear search" onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2">
          <label htmlFor="conversationspage-1" className="text-sm font-medium text-foreground">Status:</label>
          <select id="conversationspage-1" aria-label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Tag filter */}
        {orgTags.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="conversationspage-2" className="text-sm font-medium text-foreground">Label:</label>
            <select id="conversationspage-2" aria-label="Label"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
            >
              <option value="">All</option>
              {orgTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        )}

        <span className="text-sm text-muted-foreground ml-auto">
          {conversations.length} conversation{conversations.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-primary/15 border border-primary rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-link">
            {selectedIds.size} selected
          </span>
          <select aria-label="Bulk action"
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            className="border border-primary rounded-md px-3 py-1.5 text-sm bg-card focus:ring-2 focus:ring-ring"
          >
            <option value="">Choose action...</option>
            <option value="resolve">Resolve</option>
            <option value="close">Close</option>
            <option value="reopen">Re-open</option>
            <option value="assign">Assign to...</option>
            <option value="urgent">Mark Urgent</option>
          </select>
          {bulkAction === 'assign' && (
            <select aria-label="Assign to"
              value={bulkAssignee}
              onChange={(e) => setBulkAssignee(e.target.value)}
              className="border border-primary rounded-md px-3 py-1.5 text-sm bg-card focus:ring-2 focus:ring-ring"
            >
              <option value="">Select agent...</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.display_name || agent.email}
                </option>
              ))}
            </select>
          )}
          <Button variant="ghost"
            onClick={handleBulkAction}
            disabled={!bulkAction || (bulkAction === 'assign' && !bulkAssignee)}
            className="px-4 py-1.5 bg-primary text-primary-foreground text-sm rounded-md hover:bg-primary disabled:bg-primary transition-colors"
          >
            Apply
          </Button>
          <Button variant="ghost"
            onClick={() => {
              setSelectedIds(new Set());
              setBulkAction('');
            }}
            className="text-sm text-link hover:text-link ml-auto"
          >
            Cancel
          </Button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={conversations}
        loading={loading}
        emptyMessage="No conversations found"
        onRowClick={(conv) => navigate(`/conversations/${conv.id}`)}
        keyExtractor={(conv) => conv.id}
      />
    </div>
  );
}
